import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import HorizontalCard from '../../../components/v2/HorizontalCard'
import RemainingCompanies from '../../../components/v2/RemainingCompanies'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import RadioButton from '../../../components/v2/buttons/RadioButton'
import WelcomerProgressbar from '../../../components/v2/progrees-bars/WelcomerProgressbar'
import { currencySymbol } from '../../../domain/types'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { WelcomerState, setCurrency, setCurrencyId, setLimit, setLimitId } from '../../../store/welcomerSlice'

const WelcomerCurrencyAndLimit = () => {
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const dispatch = useDispatch()
  //
  const { filter, screenHeader } = useAppSelector((data: { welcomer: WelcomerState }) => data.welcomer)

  const { data: welcomerCurrencies, isLoading: isWelcomerCurrenciesLoading } =
    api.useGetWelcomerCurrencyListQuery(undefined)

  const { data: pageInfo, isLoading: isPageInfoLoading } =
    api.useGetWelcomerPagesInfoQuery('welcomer-currency-and-limit')
  //
  const pageTitle = pageInfo?.translations?.[lang]?.title
  const stepName = pageInfo?.translations?.[lang]?.stepName
  //
  const { data: WelcomerLimits, isLoading: isWelcomerLimitsLoading } = api.useGetWelcomerLimitListQuery(undefined)
  //
  const [getLeftCompanies, { data: leftCompanies }] = api.useWelcomerLeftCompaniesMutation()
  //
  useEffect(() => {
    if (filter.productCategory && filter.currency && filter.singleDates?.start && filter.singleDates?.end) {
      const GetCompaniesFunction = async () => {
        try {
          //
          await getLeftCompanies({
            step: filter.limit ? 'step-two' : 'step-one',
            productCategory: filter.productCategory,
            startDate: filter.singleDates?.start as string,
            endDate: filter.singleDates?.end as string,
            currency: filter.currency,
            limit: filter.limit as number,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (GetCompaniesFunction) (CurrencyAndLimit)')
          }
        }
      }
      //
      GetCompaniesFunction()
    }

    return () => {}
  }, [
    filter.productCategory,
    filter.singleDates?.start,
    filter.singleDates?.end,
    filter.currency,
    filter.limit,
    getLeftCompanies,
  ])
  //
  // auto navigate first page
  useEffect(() => {
    if (!filter.singleDates?.start) {
      navigate('/welcomer-start-date')
    }
    //
    return () => {}
  }, [filter.singleDates?.start, navigate])
  //
  //
  const MainLoading = isWelcomerCurrenciesLoading || isWelcomerLimitsLoading || isPageInfoLoading
  //

  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />
      <Content>
        <Title text={pageTitle} />

        <WelcomerProgressbar stepName={stepName} />

        <Block>
          <Cards>
            {pageInfo &&
              pageInfo?.listItems
                ?.filter((x) => x.type === 'gel')
                .map((item, index) => {
                  return (
                    <HorizontalCard
                      className="horizontal-card"
                      key={index}
                      title={item?.title}
                      imgUrl={item?.image}
                      selected={item.type?.toLowerCase() === filter.currency ? true : false}
                      onClick={() => {
                        if (item.type) {
                          dispatch(setCurrency(item.type?.toLowerCase()))
                          //
                          const currencyId = welcomerCurrencies?.find(
                            (x) => x.currencyCode.toLowerCase() === item.type?.toLowerCase(),
                          )
                          if (currencyId !== undefined) {
                            dispatch(setCurrencyId(currencyId?.id))
                          }
                          //
                          if (filter.currency !== item.type.toLowerCase()) {
                            dispatch(setLimit(null))
                          }
                        }
                      }}
                    >
                      <LimitDiv>
                        <hr />
                        <LimitTitle>{t('chooseLimit')}</LimitTitle>
                        <LimitDescription>{t('maximumLimit')}</LimitDescription>
                        <CheckboxBlock>
                          <RadioButton
                            size="small"
                            defaultOption={filter.limit}
                            onChange={(value) => {
                              let limitId = WelcomerLimits?.filter((x) => x.isActive === true).find(
                                (x) => x.value === Number(value),
                              )
                              dispatch(setLimit(Number(value)))
                              limitId && dispatch(setLimitId(limitId?.id))
                            }}
                            options={
                              WelcomerLimits
                                ? [
                                    ...WelcomerLimits?.filter((x) => x.isActive === true).map((item) => ({
                                      value: item.value,
                                      label:
                                        item.value?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') +
                                        ` ${currencySymbol('gel')}`,
                                    })),
                                  ]
                                : []
                            }
                          />
                        </CheckboxBlock>
                      </LimitDiv>
                    </HorizontalCard>
                  )
                })}
          </Cards>
          <RemainingCompanies
            style={{
              opacity: leftCompanies ? '1' : '0',
            }}
            text={`${t('offerAvailable')}: ${leftCompanies?.length} `}
          />
        </Block>
        <Buttons>
          <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={() => navigate('/welcomer-start-date')} />
          <NavigationButton
            text={`${t('next')}`}
            buttonType="next"
            disabled={
              filter.productCategory &&
              filter.currency &&
              filter.limit &&
              leftCompanies &&
              leftCompanies.length >= 1 &&
              filter.singleDates?.start &&
              filter.singleDates?.end
                ? false
                : true
            }
            onClick={() => navigate('/welcomer-add-travelers')}
          />
        </Buttons>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
// styles
const Block = styled.div`
  max-width: 464px;
  width: 100%;
  margin-inline: auto;
`
const CheckboxBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(2, 1fr);
  grid-column-gap: 0px;
  grid-row-gap: 10px;
`
const LimitTitle = styled.p`
  font-size: 18px;
  color: ${Colors.primaryBlack};
  margin-bottom: 1px;
`
const LimitDescription = styled.p`
  font-size: 14px;
  color: ${Colors.nevadaGrey};
  margin-bottom: 18px;
`
const LimitDiv = styled.div``

const Buttons = styled.div`
  display: flex;
  gap: 20px;
  width: min-content;
  margin-top: 48px;
  margin-inline: auto;
  button {
    width: 175px;
  }

  ${Responsive.mobile} {
    margin-top: 40px;
    gap: 16px;
    button {
      width: 140px;
    }
  }
`

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
  max-width: 464px;
  width: 100%;

  .horizontal-card-content {
    align-items: center;
  }

  .horizontal-card {
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0px;
    }
  }

  ${Responsive.mobile} {
    margin-top: 24px;

    .horizontal-card {
      margin-bottom: 20px;
      &:last-child {
        margin-bottom: 0px;
      }
    }
  }
`
const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  /*  */
`

export default WelcomerCurrencyAndLimit
